import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import {
  createQueryOptions,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingApprovalState,
  BiddingBidSendErrorCategory,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type {
  BiddingIntradayOrderFormula,
  BiddingIntradayOrderOrderMethod,
  BiddingIntradayOrderWithdrawFailureReason,
  BiddingIntradayOrderWithdrawState,
  BiddingIntradayOrderListItem as ProtoListItem,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import { listBiddingIntradayOrders } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import {
  type ListBiddingIntradayOrdersRequest,
  ListBiddingIntradayOrdersRequestSchema,
  type ListBiddingIntradayOrdersResponse,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import { type UseQueryResult, useQueries } from '@tanstack/react-query'

/**
 * 時間前注文 一覧表示行型。
 *
 * `gcAt` は GC 判定で現在時刻と比較するため Timestamp のまま保持する。
 */
export type IntradayOrderRow = {
  id: string
  targetDate: ProtoDate | undefined
  areaCode: AreaCode
  timeCode: number
  direction: Side
  priceYenPerKwh: Decimal | undefined
  volumeMwh: Decimal | undefined
  bidStatus: BiddingIntradayOrderStatus
  // 発注主体 (= どの名義で JEPX に発注したか、 issue #5366)。
  issuerType: LicenseType
  // 承認軸 (= 下書き / 承認申請中 / 承認済 等)。 一覧で承認ステータスを表示する。
  approvalState: BiddingApprovalState
  // 送信軸 (eSLJ 送信状態) / 約定結果。 承認軸・約定軸と直交する。
  sendStatus: BiddingBidSendStatus
  // 送信失敗の理由カテゴリ / 生 detail (= sendStatus が失敗系のときのみ意味を持つ)。
  sendErrorCategory: BiddingBidSendErrorCategory
  sendErrorDetail: string
  matchedVolumeMwh: Decimal | undefined
  matchedPriceYenPerKwh: Decimal | undefined
  // 最終約定時刻 (札の約定のうち最も遅い約定日時。 約定が無い札は未設定、 AC-BIN-024)。
  lastMatchedAt?: Timestamp
  // GC 時刻。 編集ボタン活性制御に使用。 Timestamp のまま保持。
  gcAt: Timestamp | undefined
  createdBy: string | undefined
  // 申請者 (REQUEST 遷移時の操作者)。 AC-BIN-098 の自己承認判定に使う。
  requestedBy?: string
  // 承認待ちの札が今待っている承認段階の承認者 (承認申請時に写した段)。 AC-BIN-098 の
  // 承認者判定に使う。 承認待ち以外の札は空。
  currentStageApproverMemberIds?: readonly string[]
  createdAt: Timestamp | undefined
  updatedBy: string | undefined
  // optimistic lock 用に proto Timestamp で保持。表示時のみ timestampDate で Date 変換。
  updatedAt: Timestamp | undefined
  // 価格種別 (指値 / 成行、 intraday-bidding.md AC-BIN-004d)。 成行の場合
  // priceYenPerKwh は "0" で来る (SpotBid と同じ契約、 risk M-13 と同種の誤表示を防ぐ)。
  orderMethod: BiddingIntradayOrderOrderMethod
  /**
   * 札を作成したワークフローの業務ID。 ワークフローを経由せずに作った札は undefined
   * (AC-BIN-018 のワークフローフィルタで使用)。
   */
  workflowId: string | undefined
  /** 詳細画面への遷移キー。表示用の業務 ID とは別の UUID。 */
  workflowCaseId?: string
  // 板取消の進行状態 (AC-BIN-008b)。 bidStatus とは別軸。
  withdrawState?: BiddingIntradayOrderWithdrawState
  // 板取消の失敗確定の理由 (AC-BIN-008b)。 withdrawState=FAILED の時だけ非 UNSPECIFIED。
  withdrawFailureReason?: BiddingIntradayOrderWithdrawFailureReason
  // 取下 (板取消 / 予約取消) を今受け付けるか。 backend が状態と GC から判定した値で、
  // 取下ボタンはこの値で出す。
  withdrawable: boolean
  // 「下書きに戻す」 (AC-BIN-115) を今受け付けるか。 backend が送信中・GC 前・確定済ワークフロー
  // 経由でないことから判定した値で、 下書きに戻すボタンはこの値で出す。
  returnableToDraft: boolean
  // GC を過ぎて JEPX が受け付けたかを確かめている札か (AC-BIN-115)。 backend が送信の終わり方・
  // JEPX 入札番号・確かめる期限から判定した値で、 送信中の補足 (「JEPX が受け付けたか確かめています」) はこの値で出す。
  receiptChecking?: boolean
  // 承認・差戻し (AC-BIN-095) を今受け付けるか。 backend が状態と GC から判定した値で
  // (AC-BIN-099)、 承認待ちの行の「承認する」「差戻し」はこの値が false の時に無効化する。
  approvalActionable: boolean
  // JEPX の入札 ID (backend が値を持つ札だけ。 無い札は未設定、 AC-BIN-003 の一覧列)。
  jepxBidNo: string | undefined
  // 自動取消 (AC-BIN-017) の予約時刻 = 取下予定日時 (AC-BIN-003 の一覧列)。
  withdrawAt: Timestamp | undefined
  // 残数量 (MW、 注文量 − 約定量。 AC-BIN-003 の一覧列)。
  remainingVolumeMw: Decimal | undefined
  // JEPX 受渡契約コード (AC-BIN-003 の一覧列)。 eBalance で作った注文は作成時に決めて保存した
  // コード、 取り込んだ札 (AC-BIN-106) は取り込み元のコードそのもの。 保存前の旧い注文は未設定。
  deliveryContractCd: string | undefined
  // メモ (内部用の自由記述、 AC-BIN-003 の一覧列)。
  memoFree: string | undefined
  // JEPX自由記述欄 (AC-BIN-003 の一覧列)。
  memoJepx: string | undefined
  // フォーミュラ価格の入力値 (AC-BIN-005)。 フォーミュラで保存した札だけ持つ。 提出確認モーダルが
  // 保存時点の評価値を併記する札を見分けるのに使う (AC-BIN-009)。
  formula?: BiddingIntradayOrderFormula
  // この札の約定の JEPX 約定番号 (AC-BIN-022a の約定結果テーブルの列)。 backend が約定日時の昇順で
  // 返した順のまま持つ。 約定が台帳に無い札は空。 includeJepxExecutionNos を付けて取った一覧の
  // 行でだけ入り、 付けない一覧の行は約定済みの札でも常に空。
  jepxExecutionNos: string[]
  // 取り込み元で見つからなくなった入札中の札の印 (AC-BIN-127)。 ステータスは変えずに
  // backend が返す。 `needsReviewSince` は印が付いた時刻 (要確認でない札は未設定)。
  needsReview?: boolean
  needsReviewSince?: Timestamp
}

function convertItem(item: ProtoListItem): IntradayOrderRow {
  return {
    id: item.id,
    targetDate: item.targetDate,
    areaCode: item.areaCode,
    timeCode: item.timeCode,
    direction: item.direction,
    priceYenPerKwh: item.priceYenPerKwh,
    volumeMwh: item.volumeMwh,
    bidStatus: item.bidStatus,
    issuerType: item.issuerType,
    approvalState: item.approvalState,
    sendStatus: item.sendStatus,
    sendErrorCategory: item.sendErrorCategory,
    sendErrorDetail: item.sendErrorDetail,
    matchedVolumeMwh: item.matchedVolumeMwh,
    matchedPriceYenPerKwh: item.matchedPriceYenPerKwh,
    lastMatchedAt: item.lastMatchedAt,
    gcAt: item.gcAt,
    createdBy: item.createdBy,
    requestedBy: item.requestedBy,
    currentStageApproverMemberIds: item.currentStageApproverMemberIds,
    createdAt: item.createdAt,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    orderMethod: item.orderMethod,
    workflowId: item.workflowId,
    workflowCaseId: item.workflowCaseId,
    withdrawState: item.withdrawState,
    withdrawFailureReason: item.withdrawFailureReason,
    withdrawable: item.withdrawable,
    returnableToDraft: item.returnableToDraft,
    receiptChecking: item.receiptChecking,
    approvalActionable: item.approvalActionable,
    jepxBidNo: item.jepxBidNo,
    withdrawAt: item.withdrawAt,
    remainingVolumeMw: item.remainingVolumeMw,
    deliveryContractCd: item.deliveryContractCd,
    memoFree: item.memoFree,
    memoJepx: item.memoJepx,
    formula: item.formula,
    jepxExecutionNos: item.jepxExecutionNos,
    needsReview: item.needsReview,
    needsReviewSince: item.needsReviewSince,
  }
}

export function convertListIntradayResponse(
  resp: ListBiddingIntradayOrdersResponse,
): IntradayOrderRow[] {
  return resp.items.map(convertItem)
}

// 送信状態の遷移 (送信待ち → 送信中 → 提出済) を polling 反映する (AC-014)。
const LIST_REFETCH_INTERVAL_MS = 5_000

const listRequest = (
  targetDateFilter?: ProtoDate,
  areaCodeFilter?: AreaCode,
  includeJepxExecutionNos?: boolean,
): ListBiddingIntradayOrdersRequest =>
  create(ListBiddingIntradayOrdersRequestSchema, {
    targetDateFilter,
    areaCodeFilter,
    includeJepxExecutionNos,
  })

/**
 * @param options.throwOnError 既定の `ApiClientProvider` は `throwOnError: true`
 *   (= error boundary に飛ばす)。 約定結果タブのように error をインライン表示したい
 *   呼び出し側は `false` を渡す (= `useQuery().error` を観測する形にする)。
 * @param options.enabled false のとき query (polling 含む) を発火しない。 省略時は
 *   有効 (= 従来どおり)。 ワークフローケース由来で開いた画面がケース対象日確定まで
 *   受渡日依存の一覧取得を止める用途 (AC-WFW-006a Decision 1)。
 * @param options.includeJepxExecutionNos true のとき各札に JEPX 約定番号
 *   (`jepxExecutionNos`) を載せて返す。 backend は札の履歴に比例して増える約定明細台帳を
 *   読むので、 約定結果テーブルの列 (AC-BIN-022a) を出す呼び出しだけが渡す。 省略時は
 *   `jepxExecutionNos` が空で返る。
 */
export function useListBiddingIntradayOrders(
  targetDateFilter?: ProtoDate,
  areaCodeFilter?: AreaCode,
  options?: {
    throwOnError?: boolean
    enabled?: boolean
    includeJepxExecutionNos?: boolean
  },
): UseQueryResult<ListBiddingIntradayOrdersResponse, ConnectError> {
  return useQuery(
    listBiddingIntradayOrders,
    listRequest(
      targetDateFilter,
      areaCodeFilter,
      options?.includeJepxExecutionNos,
    ),
    // throwOnError は未指定なら key 自体を渡さず QueryClient 既定 (= true) に従う
    // (= `throwOnError: undefined` を渡すと既定 true を上書きして error boundary が無効化されるため)。
    {
      refetchInterval: LIST_REFETCH_INTERVAL_MS,
      ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
      ...(options?.throwOnError !== undefined
        ? { throwOnError: options.throwOnError }
        : {}),
    },
  )
}

/** 複数の受渡日の注文の取得結果。 */
export type IntradayOrdersByDates = {
  /** 取得できた受渡日の注文 (取得中の受渡日の分は含まれない)。 */
  orders: IntradayOrderRow[]
  /** 渡した受渡日すべての取得が終わったか (受渡日が 0 件なら true)。 */
  isLoaded: boolean
}

// `combine` は参照が変わるたびに再計算されるので、 module に置いて固定する。
const combineOrdersByDates = (
  results: ReadonlyArray<{
    data: ListBiddingIntradayOrdersResponse | undefined
  }>,
): IntradayOrdersByDates => ({
  orders: results.flatMap((result) =>
    result.data !== undefined ? convertListIntradayResponse(result.data) : [],
  ),
  isLoaded: results.every((result) => result.data !== undefined),
})

/**
 * 複数の受渡日の注文をまとめて取る。 受渡日ごとの query は
 * `useListBiddingIntradayOrders(targetDate)` と同じ key・同じ polling なので、
 * 入札追加タブと cache を共有する。
 *
 * 取得の失敗は、 その受渡日の注文を一度も読めていない時だけ画面のエラーにする
 * (注文を読めないまま「該当なし」として先へ進ませないため)。 一度読めた後の再取得
 * (polling) の失敗では、 最後に読めた結果を返し続け、 画面を置き換えない。 取得が
 * 終わったかは `isLoaded` で返す。
 */
export function useListBiddingIntradayOrderRowsByDates(
  targetDates: readonly ProtoDate[],
): IntradayOrdersByDates {
  const transport = useTransport()
  return useQueries({
    queries: targetDates.map((targetDate) => ({
      ...createQueryOptions(
        listBiddingIntradayOrders,
        listRequest(targetDate),
        { transport },
      ),
      refetchInterval: LIST_REFETCH_INTERVAL_MS,
      // 利用側 (CSV 取込の Step2) は、 確定の要求が走っている間もこの一覧を読み直す。
      // その再取得の失敗で画面ごとエラーに置き換えると、 確定の成否が分からなくなる。
      // 書き方は `useListWorkflowCases` と同じ。
      throwOnError: (
        _error: unknown,
        query: { state: { data?: unknown } },
      ): boolean => query.state.data === undefined,
    })),
    combine: combineOrdersByDates,
  })
}
